import { System, SystemImpl } from "minecs";
import type { GameModel } from "yage/game/GameModel";
import { Transform } from "yage/schemas/entity/Transform";
import { AirportState, AirportAgent, AirportStaff, AirportPassenger } from "../components/index.js";
import { STREET_ENTRY, ENTRANCE, ENTRANCE_DOOR, PASSENGER_WALK_SPEED, CONFIG } from "../domain/config.js";
import { shiftsActive } from "../domain/act2.js";
import { tierIndex, tier } from "../domain/airports.js";
import { deposit } from "../domain/economy.js";
import { worksDesk } from "../services/crew.js";
import { CRISES, CRISIS_REWARD_CAP, CRISIS_SELF_RESOLVE, CRISIS_SLOTS, EVENT_KINDS, EVENT_LEAD, EVENT_LENGTH, FERRY_MAX, FERRY_MIN, CHARTER_GROUP, crisisCap, crisisPick, crisisSchedule, shiftEvent } from "../domain/events.js";
import { crisisPoint, slotKeys } from "../services/events.js";
import { currentMission } from "../domain/missions.js";
import { addDiary } from "../services/diary.js";
import { distance, ids, make, notice, state, stations } from "../services/world.js";

/** Act II events (forecast, then a rush, storm or ferry) and crises (spawn, auto-resolve, captain fix). */
@System(AirportState)
export class AirportEvents extends SystemImpl<GameModel> {
    static depth = 4860;
    run = (g: GameModel) => {
        const e = state(g);
        if (!e.loaded || !shiftsActive(e)) return;
        const t = e.shiftFrames, port = tierIndex(e), ss0 = () => stations(g);
        // Events: one per shift, forecast EVENT_LEAD frames ahead.
        if (t === 1 || e.eventAt === 0) {
            const ev = shiftEvent(e.shift, port, tier(e).code === 'BVM');
            e.eventKind = ev.kind; e.eventAt = ev.at; e.eventAnnounced = 0; e.eventDone = 0;
        }
        if (e.eventKind && !e.eventAnnounced && t >= e.eventAt - EVENT_LEAD) {
            e.eventAnnounced = 1;
            notice(g, `Forecast: ${EVENT_KINDS[e.eventKind]} in about ${Math.round(EVENT_LEAD / 300)} airport minutes.`, 2);
        }
        if (e.eventKind === 3 && !e.eventDone && t >= e.eventAt) {
            // A ferry lands a wave of tourists at once.
            e.eventDone = 1;
            const count = FERRY_MIN + e.shift % (FERRY_MAX - FERRY_MIN + 1), room = Math.max(0, CONFIG.maxPassengers - ids(g, AirportPassenger).length);
            e.ferryFirst = e.serial + 1; e.ferryLast = e.serial + Math.min(count, room); e.ferryWalkouts = 0;
            for (let n = 0; n < Math.min(count, room); n++) {
                const serial = ++e.serial;
                make(g, 'Passenger', { Transform: { x: STREET_ENTRY.x + (n % 5) * 12, y: STREET_ENTRY.y + Math.floor(n / 5) * 14 },
                    AirportPassenger: { stage: -1, entering: true, serial, tint: g.rand.int(0, 5), vip: serial % 9 === 0 },
                    AirportPath: { speed: PASSENGER_WALK_SPEED, moving: true, targetX: STREET_ENTRY.x, targetY: ENTRANCE.y, points: [STREET_ENTRY.x, ENTRANCE_DOOR.y, STREET_ENTRY.x, ENTRANCE.y] } });
            }
            notice(g, `The ferry is in: ${Math.min(count, room)} tourists are heading for Check-In.`, 2);
            e.ferryBase = e.shiftWalkouts;
        }
        // MGT-023: a ferry wave counts as clean when, six airport minutes after it lands, none of its tourists walked out.
        if (e.eventKind === 3 && e.eventDone && t === e.eventAt + 1800 && e.ferryWalkouts === 0 && e.ferryLast >= e.ferryFirst && !e.ferryClean) {
            e.ferryClean = 1;
            notice(g, 'Clean ferry day: every tourist made it through without a walkout!', 2);
            addDiary(g, `Ferry day at ${tier(e).name}: ${e.ferryLast - e.ferryFirst + 1} tourists, not one walkout.`);
        }
        if (e.eventKind === 6 && !e.eventDone && t >= e.eventAt) {
            // A group charter (the Pinecones!) lands six travellers together.
            e.eventDone = 1;
            const room = Math.max(0, CONFIG.maxPassengers - ids(g, AirportPassenger).length);
            for (let n = 0; n < Math.min(CHARTER_GROUP, room); n++) {
                const serial = ++e.serial;
                make(g, 'Passenger', { Transform: { x: STREET_ENTRY.x + (n % 3) * 12, y: STREET_ENTRY.y + Math.floor(n / 3) * 14 },
                    AirportPassenger: { stage: -1, entering: true, serial, tint: 2, vip: false },
                    AirportPath: { speed: PASSENGER_WALK_SPEED, moving: true, targetX: STREET_ENTRY.x, targetY: ENTRANCE.y, points: [STREET_ENTRY.x, ENTRANCE_DOOR.y, STREET_ENTRY.x, ENTRANCE.y] } });
            }
            notice(g, 'The Pinecones group charter is here: six travellers checking in together.', 2);
        }
        if (e.eventKind && !e.eventDone && e.eventKind !== 3 && e.eventKind !== 6 && t >= e.eventAt + EVENT_LENGTH) { e.eventDone = 1; notice(g, `${EVENT_KINDS[e.eventKind]} is over.`); }

        // MGT-022: the Bayview takeover's "Calm the belt" beat brings one calm belt jam to fix by hand.
        if (currentMission(e)?.metric === 'crisesFixed' && !slotKeys().some(k => e[k.type] > 0) && ss0()[1]?.unlocked) {
            Object.assign(e, { crisis0: 1, crisisAge0: 0, crisisHold0: 0, crisisGate0: 0 });
            notice(g, `${CRISES[0].name}! Stand at the red marker by Bag Drop to clear it; your crew watches how it's done.`, 2);
        }
        // Crises: roll at scheduled frames, respecting the active cap, per-type once a shift and unlocked departments.
        const schedule = crisisSchedule(e.shift, port, e.relaxed), ss = stations(g), captains = ids(g, AirportAgent).length;
        while (e.crisesRolled < schedule.length && t >= schedule[e.crisesRolled]) {
            const type = crisisPick(e.shift, e.crisesRolled, port), c = CRISES[type];
            e.crisesRolled++;
            const active = slotKeys().filter(k => e[k.type] > 0).length, free = slotKeys().find(k => e[k.type] === 0);
            if (!free || active >= crisisCap(captains) || e.crisisTypes & (1 << type) || !ss[c.station]?.unlocked) continue;
            e.crisisTypes |= 1 << type;
            Object.assign(e, { [free.type]: type + 1, [free.age]: 0, [free.hold]: 0, [free.gate]: c.effect === 'gate' ? e.crisesRolled % Math.max(1, ss[6].lanes) : 0 });
            notice(g, `${c.name}! ${c.text} A captain can fix it on the spot; staff will in time.`, 2);
        }
        for (const k of slotKeys()) {
            if (!e[k.type]) continue;
            const c = CRISES[e[k.type] - 1], at = crisisPoint(c.station);
            e[k.age]++;
            const staffed = ss[c.station]?.activeWorkers > 0 || ids(g, AirportStaff).some(id => { const s = g.getTypedUnsafe(AirportStaff, id); return worksDesk(s) && s.stage === c.station; });
            const near = ids(g, AirportAgent).some(a => distance(g.getTypedUnsafe(Transform, a), at) < 60);
            e[k.hold] = near ? e[k.hold] + 1 : 0;
            if (near && e[k.hold] >= c.captainFix) {
                let reward = 0;
                if (e.crisisRewards < CRISIS_REWARD_CAP) {
                    e.crisisRewards++;
                    reward = deposit(e, ss[c.station] ?? ss[0], Math.max(20, Math.min(400, Math.round(e.shiftEarned / Math.max(60, e.shiftFrames / 60) * 30))));
                    e.shiftEarned += reward;
                }
                e.crisesFixed++;
                addDiary(g, `A captain fixed the ${c.name.toLowerCase()} at ${tier(e).name} in ${Math.round(e[k.hold] / 60)} seconds.`);
                notice(g, `${c.name} fixed by a captain${reward ? ` · +${reward} bills on the floor` : ''}.`, 1);
                e[k.type] = 0;
            } else if (staffed && e[k.age] >= c.staffFix || e[k.age] >= CRISIS_SELF_RESOLVE) {
                notice(g, `${c.name} resolved.`);
                e[k.type] = 0;
            }
        }
    };
}
export { CRISIS_SLOTS };
